import { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Gem, TrendingUp, TrendingDown, GitCompare, Layers, DollarSign,
  RefreshCw, AlertTriangle, Info, Activity, Scale,
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
  BarChart, Bar, Cell,
} from 'recharts';
import { Spinner, InfoTip } from '../components/ui';
import {
  getPortfolioBenchmark, getPortfolioHodlVsTrade,
  getPortfolioCorrelation, getPortfolioFeeImpact,
} from '../services/api';

const TABS = [
  { id: 'benchmark', label: 'Vs Benchmark', icon: TrendingUp },
  { id: 'hodl',      label: 'HODL vs Trade', icon: GitCompare },
  { id: 'corr',      label: 'Correlation',   icon: Layers },
  { id: 'fees',      label: 'Fee Impact',    icon: DollarSign },
];

function Card({ children, className = '' }) {
  return <div className={`bg-dark-800 rounded-2xl p-6 border border-dark-700/50 ${className}`}>{children}</div>;
}
function fmtUsd(v, dp = 2) {
  if (v == null || !Number.isFinite(v)) return '—';
  const sign = v < 0 ? '-' : '';
  return `${sign}$${Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp })}`;
}
function fmtPct(v, dp = 2) {
  if (v == null || !Number.isFinite(v)) return '—';
  return `${v > 0 ? '+' : ''}${v.toFixed(dp)}%`;
}
function pnlClass(v) {
  if (v == null || !Number.isFinite(v) || v === 0) return 'text-dark-400';
  return v > 0 ? 'text-green-400' : 'text-red-400';
}

// ─── Benchmark tab ───────────────────────────────────────────────────────────

const BENCHMARK_PRESETS = ['BTC', 'ETH', 'SOL', 'AVAX', 'LINK', 'MATIC', 'DOGE', 'ADA'];

function BenchmarkTab() {
  const [benchmark, setBenchmark] = useState('BTC');
  const [days, setDays] = useState(90);
  const [customInput, setCustomInput] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setData(await getPortfolioBenchmark({ benchmark, days })); }
    catch (e) { setError(e?.message || 'Failed to load benchmark'); }
    finally { setLoading(false); }
  }, [benchmark, days]);

  useEffect(() => { load(); }, [load]);

  const applyCustom = () => {
    const v = customInput.trim().toUpperCase();
    if (v && /^[A-Z0-9]{2,10}$/.test(v)) {
      setBenchmark(v);
      setCustomInput('');
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[280px]">
            <div className="text-xs text-dark-400 uppercase tracking-wide mb-2">Benchmark</div>
            <div className="flex flex-wrap gap-1.5 items-center">
              {BENCHMARK_PRESETS.map(b => (
                <button key={b} onClick={() => setBenchmark(b)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium border ${
                    benchmark === b
                      ? 'bg-blue-600 text-white border-blue-500'
                      : 'bg-dark-900 text-dark-300 hover:text-dark-100 border-dark-700/50'
                  }`}>{b}</button>
              ))}
              {!BENCHMARK_PRESETS.includes(benchmark) && (
                <span className="px-3 py-1.5 rounded-md text-sm font-medium bg-blue-600 text-white border border-blue-500">
                  {benchmark}
                </span>
              )}
              <div className="flex items-center gap-1 ml-1">
                <input
                  type="text" value={customInput} onChange={e => setCustomInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && applyCustom()}
                  placeholder="Other symbol..."
                  className="px-2.5 py-1.5 text-sm rounded-md bg-dark-900 border border-dark-700/50 focus:outline-none focus:border-blue-500 w-32"
                  maxLength={10}
                />
                <button onClick={applyCustom} disabled={!customInput.trim()}
                  className="px-2.5 py-1.5 text-xs rounded-md bg-dark-700 hover:bg-dark-600 disabled:opacity-50">
                  Set
                </button>
              </div>
            </div>
          </div>
          <div>
            <div className="text-xs text-dark-400 uppercase tracking-wide mb-2">Window</div>
            <div className="inline-flex rounded-lg bg-dark-900 p-1 border border-dark-700/50">
              {[30, 60, 90, 180].map(d => (
                <button key={d} onClick={() => setDays(d)}
                  className={`px-3 py-2 rounded-md text-sm font-medium ${
                    days === d ? 'bg-blue-600 text-white' : 'text-dark-300 hover:text-dark-100'
                  }`}>{d}d</button>
              ))}
            </div>
          </div>
          <button onClick={load} disabled={loading}
            className="ml-auto px-3 py-2 bg-dark-900 hover:bg-dark-700 rounded-lg text-sm flex items-center gap-2 border border-dark-700/50 disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </Card>

      {loading && !data && <div className="flex justify-center py-12"><Spinner /></div>}
      {error && <Card><div className="flex gap-3 items-start"><AlertTriangle className="w-5 h-5 text-red-400 mt-0.5" /><div className="text-sm text-red-300">{error}</div></div></Card>}

      {data && data.dataSource === 'insufficient' && (
        <Card><div className="flex items-start gap-3"><Info className="w-5 h-5 text-amber-400 mt-0.5" />
          <div className="text-sm text-amber-200">{data.message}</div></div></Card>
      )}

      {data && data.dataSource === 'partial' && (
        <Card><div className="flex items-start gap-3"><AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5" />
          <div className="text-sm text-amber-200">{data.message} Try one of the headline benchmarks (BTC/ETH/SOL).</div></div></Card>
      )}

      {data && data.dataSource !== 'insufficient' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card><div className="text-xs text-dark-400 uppercase tracking-wide mb-1 flex items-center gap-1.5">
              Your return
              <InfoTip side="bottom">Your realized portfolio return over the window, computed from real fills via the FIFO cost-basis engine.</InfoTip>
            </div>
              <div className={`text-2xl font-bold ${pnlClass(data.userReturnPct)}`}>{fmtPct(data.userReturnPct)}</div></Card>
            <Card><div className="text-xs text-dark-400 uppercase tracking-wide mb-1 flex items-center gap-1.5">
              {benchmark} hold
              <InfoTip side="bottom">Counterfactual: what just holding {benchmark} over the same window would have returned. Compare against Your return to see if trading added value.</InfoTip>
            </div>
              <div className={`text-2xl font-bold ${pnlClass(data.benchmarkReturnPct)}`}>{fmtPct(data.benchmarkReturnPct)}</div></Card>
            <Card><div className="text-xs text-dark-400 uppercase tracking-wide mb-1 flex items-center gap-1.5">
              Outperformance
              <InfoTip side="bottom">Your return minus benchmark return. Positive = trading added value. Negative = just holding the benchmark would have done better.</InfoTip>
            </div>
              <div className={`text-2xl font-bold ${pnlClass(data.outperformancePct)}`}>{fmtPct(data.outperformancePct)}</div></Card>
            <Card><div className="text-xs text-dark-400 uppercase tracking-wide mb-1 flex items-center gap-1.5">
              Correlation · Beta · Alpha
              <InfoTip side="left">ρ (correlation): how much your returns move with the benchmark. β (beta): your portfolio's leverage to the benchmark (1 = same volatility). α (alpha): excess return after adjusting for beta — the closest thing to "true skill" in finance.</InfoTip>
            </div>
              <div className="text-sm font-mono mt-1">
                <div>ρ <span className="text-dark-200">{data.correlation != null ? data.correlation.toFixed(3) : '—'}</span></div>
                <div>β <span className="text-dark-200">{data.beta != null ? data.beta.toFixed(3) : '—'}</span></div>
                <div>α <span className="text-dark-200">{data.alpha != null ? data.alpha.toFixed(3) : '—'}</span></div>
              </div></Card>
          </div>
          <Card>
            <div className="text-sm font-semibold mb-3">Cumulative return: you vs {benchmark}</div>
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={data.series}>
                <CartesianGrid stroke="#1f2937" strokeDasharray="3 3" />
                <XAxis dataKey="date" stroke="#6b7280" tick={{ fontSize: 11 }} />
                <YAxis stroke="#6b7280" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
                <Tooltip contentStyle={{ background: '#111827', border: '1px solid #374151' }}
                  formatter={(v) => [`${v}%`, '']} />
                <Legend />
                <Line type="monotone" dataKey="userCumPct" name="You" stroke="#3b82f6" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="benchmarkCumPct" name={benchmark} stroke="#f59e0b" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
            <div className="text-xs text-dark-400 mt-3">
              Methodology: your daily realized P&amp;L ÷ deployed capital vs daily price return on {benchmark}/USDT.
              {data.benchmarkExchange && ` Prices from ${data.benchmarkExchange}.`}
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

// ─── HODL vs Trade tab ──────────────────────────────────────────────────────

function HodlVsTradeTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setData(await getPortfolioHodlVsTrade()); }
    catch (e) { setError(e?.message || 'Failed to load'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <div className="flex justify-center py-12"><Spinner /></div>;
  if (error) return <Card><div className="flex gap-3"><AlertTriangle className="w-5 h-5 text-red-400" /><div className="text-sm text-red-300">{error}</div></div></Card>;
  if (data?.dataSource === 'insufficient') return <Card><div className="flex gap-3"><Info className="w-5 h-5 text-amber-400" /><div className="text-sm text-amber-200">{data.message}</div></div></Card>;

  const totals = data?.totals || {};
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card><div className="text-xs text-dark-400 uppercase tracking-wide mb-1">If you'd HODL'd</div>
          <div className="text-2xl font-bold text-amber-400">{fmtUsd(totals.hodlValue)}</div>
          <div className="text-xs text-dark-400 mt-1">Worth today, every coin you ever bought</div></Card>
        <Card><div className="text-xs text-dark-400 uppercase tracking-wide mb-1">Your actual outcome</div>
          <div className="text-2xl font-bold text-blue-400">{fmtUsd(totals.actualValue)}</div>
          <div className="text-xs text-dark-400 mt-1">Realized P&amp;L + open lot value at spot</div></Card>
        <Card><div className="text-xs text-dark-400 uppercase tracking-wide mb-1">Trading edge</div>
          <div className={`text-2xl font-bold ${pnlClass(totals.deltaUsd)}`}>{fmtUsd(totals.deltaUsd)}</div>
          <div className={`text-xs mt-1 ${pnlClass(totals.deltaPct)}`}>{fmtPct(totals.deltaPct)} vs HODL</div></Card>
      </div>

      <Card>
        <div className="text-sm font-semibold mb-3">Per-asset comparison</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-xs text-dark-400 uppercase tracking-wide border-b border-dark-700/50">
              <th className="text-left py-3">Asset</th>
              <th className="text-right py-3">Bought qty</th>
              <th className="text-right py-3">Open qty</th>
              <th className="text-right py-3">HODL value</th>
              <th className="text-right py-3">Actual value</th>
              <th className="text-right py-3">Δ vs HODL</th>
            </tr></thead>
            <tbody>
              {(data?.assets || []).map((a, i) => (
                <tr key={i} className="border-b border-dark-800/60 hover:bg-dark-900/40">
                  <td className="py-3 font-medium">{a.asset}</td>
                  <td className="text-right py-3 font-mono text-xs">{a.totalBoughtQty ?? '—'}</td>
                  <td className="text-right py-3 font-mono text-xs">{a.openQty ?? '—'}</td>
                  <td className="text-right py-3">{fmtUsd(a.hodlValue)}</td>
                  <td className="text-right py-3">{fmtUsd(a.actualValue)}</td>
                  <td className={`text-right py-3 font-medium ${pnlClass(a.deltaUsd)}`}>
                    {fmtUsd(a.deltaUsd)} <span className="text-xs text-dark-400 ml-1">({fmtPct(a.deltaPct)})</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="text-xs text-dark-400 mt-3">
          Negative delta = trading lost value vs simply holding. Positive = trading beat HODL.
        </div>
      </Card>
    </div>
  );
}

// ─── Correlation tab ────────────────────────────────────────────────────────

function CorrelationTab() {
  const [days, setDays] = useState(60);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setData(await getPortfolioCorrelation({ days, limit: 8 })); }
    catch (e) { setError(e?.message || 'Failed to load'); }
    finally { setLoading(false); }
  }, [days]);
  useEffect(() => { load(); }, [load]);

  function cellColor(v) {
    if (v == null) return 'bg-dark-800 text-dark-500';
    if (v >= 0.7) return 'bg-red-700/70 text-white';
    if (v >= 0.4) return 'bg-amber-700/60 text-white';
    if (v >= 0.0) return 'bg-yellow-900/30 text-amber-200';
    if (v >= -0.4) return 'bg-emerald-900/30 text-emerald-300';
    return 'bg-emerald-700/60 text-white';
  }

  if (loading && !data) return <div className="flex justify-center py-12"><Spinner /></div>;
  if (error) return <Card><div className="flex gap-3"><AlertTriangle className="w-5 h-5 text-red-400" /><div className="text-sm text-red-300">{error}</div></div></Card>;
  if (data?.dataSource === 'insufficient' || data?.dataSource === 'partial') {
    return <Card><div className="flex gap-3"><Info className="w-5 h-5 text-amber-400" /><div className="text-sm text-amber-200">{data.message}</div></div></Card>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <div className="flex items-center gap-4">
          <div>
            <div className="text-xs text-dark-400 uppercase tracking-wide mb-2">Window</div>
            <div className="inline-flex rounded-lg bg-dark-900 p-1 border border-dark-700/50">
              {[30, 60, 90, 180].map(d => (
                <button key={d} onClick={() => setDays(d)}
                  className={`px-3 py-2 rounded-md text-sm font-medium ${
                    days === d ? 'bg-blue-600 text-white' : 'text-dark-300 hover:text-dark-100'
                  }`}>{d}d</button>
              ))}
            </div>
          </div>
          <div className="ml-auto text-right">
            <div className="text-xs text-dark-400 uppercase tracking-wide">Diversification score</div>
            <div className="text-2xl font-bold text-blue-400">{data?.diversificationScore ?? '—'}<span className="text-sm text-dark-400">/100</span></div>
            <div className="text-xs text-dark-500">avg |ρ| = {data?.avgAbsCorrelation != null ? data.avgAbsCorrelation.toFixed(3) : '—'}</div>
          </div>
        </div>
      </Card>

      <Card>
        <div className="text-sm font-semibold mb-3">Pairwise return correlation ({data?.observations || 0} daily observations)</div>
        <div className="overflow-x-auto">
          <table className="text-sm border-collapse">
            <thead>
              <tr>
                <th className="p-2"></th>
                {(data?.symbols || []).map(s => (
                  <th key={s} className="p-2 text-xs font-semibold text-dark-300">{s}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data?.symbols || []).map((rowSym, i) => (
                <tr key={rowSym}>
                  <td className="p-2 text-xs font-semibold text-dark-300 text-right pr-3">{rowSym}</td>
                  {(data.symbols).map((colSym, j) => {
                    const v = data.matrix[i][j];
                    return (
                      <td key={colSym} className={`p-2 text-center font-mono text-xs ${cellColor(v)}`} style={{ minWidth: 60 }}>
                        {v != null ? v.toFixed(2) : '—'}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="text-xs text-dark-400 mt-3">
          1.0 = move together. -1.0 = move opposite. 0 = no relationship. Diversification helps when correlations are low.
        </div>
      </Card>
    </div>
  );
}

// ─── Fee Impact tab ─────────────────────────────────────────────────────────

function FeeImpactTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setData(await getPortfolioFeeImpact()); }
    catch (e) { setError(e?.message || 'Failed to load'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <div className="flex justify-center py-12"><Spinner /></div>;
  if (error) return <Card><div className="flex gap-3"><AlertTriangle className="w-5 h-5 text-red-400" /><div className="text-sm text-red-300">{error}</div></div></Card>;
  if (data?.dataSource === 'insufficient') return <Card><div className="flex gap-3"><Info className="w-5 h-5 text-amber-400" /><div className="text-sm text-amber-200">{data.message}</div></div></Card>;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card><div className="text-xs text-dark-400 uppercase tracking-wide mb-1 flex items-center gap-1.5">
          Fees paid
          <InfoTip side="bottom">Total CEX taker/maker fees the exchange reported on each fill. DEX gas costs are not yet captured here.</InfoTip>
        </div>
          <div className="text-2xl font-bold text-red-400">{fmtUsd(data?.totalFeesUsd)}</div></Card>
        <Card><div className="text-xs text-dark-400 uppercase tracking-wide mb-1 flex items-center gap-1.5">
          Gross P&amp;L (no fees)
          <InfoTip side="bottom">What your realized P&L would be if fees didn't exist. Useful for measuring the strategy itself separately from execution costs.</InfoTip>
        </div>
          <div className={`text-2xl font-bold ${pnlClass(data?.grossPnLUsd)}`}>{fmtUsd(data?.grossPnLUsd)}</div></Card>
        <Card><div className="text-xs text-dark-400 uppercase tracking-wide mb-1 flex items-center gap-1.5">
          Net P&amp;L
          <InfoTip side="bottom">Gross P&L minus fees. The number that actually ends up in your account.</InfoTip>
        </div>
          <div className={`text-2xl font-bold ${pnlClass(data?.totalRealizedPnLUsd)}`}>{fmtUsd(data?.totalRealizedPnLUsd)}</div></Card>
        <Card><div className="text-xs text-dark-400 uppercase tracking-wide mb-1 flex items-center gap-1.5">
          Fee drag
          <InfoTip side="left">Fees ÷ Gross P&L. &lt;5% = healthy. 5-15% = optimize venues. 15-30% = serious problem. &gt;30% = strategy doesn't work after costs.</InfoTip>
        </div>
          <div className="text-2xl font-bold text-amber-400">{data?.feeDragPct != null ? `${data.feeDragPct.toFixed(2)}%` : '—'}</div>
          <div className="text-xs text-dark-400 mt-1">of gross P&amp;L</div></Card>
      </div>

      <Card>
        <div className="text-sm font-semibold mb-3">Volume & fees by source</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-xs text-dark-400 uppercase tracking-wide border-b border-dark-700/50">
              <th className="text-left py-3">Source</th>
              <th className="text-right py-3">Trades</th>
              <th className="text-right py-3">Volume traded</th>
              <th className="text-right py-3">Realized P&amp;L</th>
            </tr></thead>
            <tbody>
              {(data?.bySource || []).map((s, i) => (
                <tr key={i} className="border-b border-dark-800/60">
                  <td className="py-3 font-medium uppercase">{s.source}</td>
                  <td className="text-right py-3">{s.trades}</td>
                  <td className="text-right py-3">{fmtUsd(s.notionalUsd)}</td>
                  <td className={`text-right py-3 font-medium ${pnlClass(s.pnlUsd)}`}>{fmtUsd(s.pnlUsd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="text-xs text-dark-400 mt-3">
          Fees include CEX taker/maker fees that exchanges report on each fill. DEX gas costs are not yet captured here.
        </div>
      </Card>
    </div>
  );
}

// ─── Page shell ─────────────────────────────────────────────────────────────

export default function QuantDeskPage() {
  const [tab, setTab] = useState('benchmark');

  return (
    <div className="min-h-screen bg-dark-900 text-dark-100 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Gem className="w-8 h-8 text-purple-400" />
              Quant Desk
            </h1>
            <p className="text-dark-400 mt-1">
              Institutional-grade portfolio analytics — benchmarked, correlated, fee-aware.
              Every number is computed from your real fills via the FIFO cost-basis engine. No mocks.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 border-b border-dark-700/50 pb-3">
          {TABS.map(t => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  tab === t.id ? 'bg-purple-600 text-white' : 'bg-dark-800 text-dark-300 hover:bg-dark-700'
                }`}>
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === 'benchmark' && <BenchmarkTab />}
        {tab === 'hodl' && <HodlVsTradeTab />}
        {tab === 'corr' && <CorrelationTab />}
        {tab === 'fees' && <FeeImpactTab />}
      </div>
    </div>
  );
}
